import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Trash2,
  Loader2,
  Paperclip,
  FileText,
  ExternalLink,
  ImagePlus,
  MessageSquare,
  Send,
  Check,
  CheckCheck,
  CheckCircle2,
  Lock,
  X,
} from 'lucide-react';
import Loader from '../../../components/common/Loader/Loader';
import Toast from '../../../components/common/Toast/Toast';
import Modal from '../../../components/common/Modal/Modal';
import { bugService } from '../../../services/bug.service';
import { useAuth } from '../../../context/AuthContext';
import { useBugReports } from '../../../context/BugReportContext.jsx';
import './BugReports.css';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const STATUS_OPTIONS = STATUS_FILTERS.filter((filter) => filter.key !== 'all');

const LIMIT = 10;

const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option.label;
  return acc;
}, {});

// Once a report is resolved or closed the thread is frozen and users cannot reopen it
const CLOSED_STATUSES = ['resolved', 'closed'];
const MAX_REPLY_LENGTH = 2000;
// Keep in sync with the bug-chatbox--closing slide-down animation
const CHATBOX_CLOSE_MS = 220;
// Admins can juggle a few threads at once, beyond this the oldest box is dropped
const MAX_OPEN_CHATBOXES = 3;
// Open boxes poll so replies and read receipts show up without a page reload.
// Faster than the sidebar badge poll - someone is looking at this conversation.
const THREAD_POLL_MS = 12000;
// Scroll stays pinned to the newest message unless the reader moved further up
const SCROLL_PIN_SLACK_PX = 200;

// Attachments shared inside a thread - same limits the report form uses, since
// both go through the same signed-URL endpoint
const MAX_CHAT_ATTACHMENTS = 5;
const MAX_CHAT_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const VALID_CHAT_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'application/pdf',
];

// URLs typed or pasted into a message become links, and the ones that point at
// an image also render inline - people paste image links instead of attaching
const URL_PATTERN = /(https?:\/\/[^\s<>"')\]]+)/g;
const IS_URL = /^https?:\/\//i;
const IMAGE_URL_PATTERN = /\.(png|jpe?g|webp|gif|avif|bmp|svg)(?:[?#]|$)/i;

// Everything a poll can change on an open thread. Compared before applying an
// update so an unchanged poll never re-renders the box (which would move its
// scroll and reset the image loads).
const threadSignature = (bug) =>
  [bug?.status, bug?.replies?.length || 0, bug?.lastReplyAt || '', bug?.othersSeenAt || ''].join('|');

const isThreadLocked = (bug) => CLOSED_STATUSES.includes(bug?.status);
const isAdminRole = (role) => role === 'admin' || role === 'super_admin';
const isImageUrl = (url) => Boolean(url) && IMAGE_URL_PATTERN.test(url);
const isImageAttachment = (attachment) =>
  attachment?.contentType?.startsWith('image/') || isImageUrl(attachment?.url);

// reportedBy.userId comes back populated, reply authors do not
const toId = (value) => (value ? String(value._id || value) : null);

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Compact stamp for the Seen receipt - the clock alone for today, with the day
 * and month once it is older, since the full date does not fit under a bubble
 */
const formatSeenTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

  if (date.toDateString() === new Date().toDateString()) {
    return time;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return `${day}/${month} ${time}`;
};

/**
 * Body of one chat bubble - message text plus whatever images it carries
 * Images arrive two ways: as uploaded attachments, or as an image URL sitting in
 * the text. Both render inline; a URL that only looks like an image (the <img>
 * fails to load) falls back to a plain link / file chip instead of a broken box.
 */
const ThreadMessageBody = ({ text, attachments }) => {
  const [brokenUrls, setBrokenUrls] = useState([]);

  // An image finishing its load grows the thread, which would push the newest
  // message out of view. Re-pin the scroll only when the reader was already at
  // the bottom before this image took up its space.
  const handleImageLoad = (e) => {
    const image = e.currentTarget;
    const body = image.closest('.bug-chatbox-body');
    if (!body) return;

    const wasAtBottom =
      body.scrollTop + body.clientHeight >= body.scrollHeight - image.offsetHeight - 200;

    if (wasAtBottom) {
      body.scrollTop = body.scrollHeight;
    }
  };

  const markBroken = (url) =>
    setBrokenUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));

  const isRenderable = (url) => Boolean(url) && !brokenUrls.includes(url);

  // split() with a capture group keeps the URLs, so text and links alternate
  const parts = text ? text.split(URL_PATTERN) : [];
  const textImages = [
    ...new Set((text?.match(URL_PATTERN) || []).filter((url) => isImageUrl(url))),
  ].filter(isRenderable);

  const files = attachments || [];

  return (
    <>
      {text && (
        <p className="bug-thread-text">
          {parts.map((part, index) =>
            IS_URL.test(part) ? (
              <a
                key={`${index}-${part}`}
                href={part}
                target="_blank"
                rel="noreferrer"
                className="bug-thread-inline-link"
              >
                {part}
              </a>
            ) : (
              part
            )
          )}
        </p>
      )}

      {(textImages.length > 0 || files.length > 0) && (
        <div className="bug-thread-media">
          {textImages.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer" className="bug-thread-media-link">
              <img
                src={url}
                alt=""
                className="bug-thread-media-image"
                loading="lazy"
                onLoad={handleImageLoad}
                onError={() => markBroken(url)}
              />
            </a>
          ))}

          {files.map((attachment, index) => {
            const label = attachment.fileName || `Attachment ${index + 1}`;

            return isImageAttachment(attachment) && isRenderable(attachment.url) ? (
              <a
                key={attachment.url}
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                className="bug-thread-media-link"
                title={label}
              >
                <img
                  src={attachment.url}
                  alt={label}
                  className="bug-thread-media-image"
                  loading="lazy"
                  onLoad={handleImageLoad}
                  onError={() => markBroken(attachment.url)}
                />
              </a>
            ) : (
              <a
                key={attachment.url}
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                className="bug-thread-media-file"
                title={label}
              >
                <FileText size={14} />
                <span className="bug-thread-media-file-name">{label}</span>
              </a>
            );
          })}
        </div>
      )}
    </>
  );
};

/**
 * One docked chat box for a single bug report thread
 * Kept at module level so typing in the composer never remounts the textarea
 */
const BugThreadChatbox = ({
  bug,
  isAdmin,
  isOwnMessage,
  draft,
  onDraftChange,
  attachments,
  uploading,
  onFilesSelected,
  onRemoveAttachment,
  sending,
  closing,
  bodyRef,
  onClose,
  onSubmit,
  onResolve,
}) => {
  const locked = isThreadLocked(bug);
  const reporterName = bug.reportedBy?.name || 'Unknown';
  const reporterEmail = bug.reportedBy?.email || '-';
  const fileInputRef = useRef(null);
  const pending = attachments || [];

  // Read receipt sits under the last thing the viewer sent, like any chat app.
  // othersSeenAt is the last time the other side opened this thread, so a
  // message older than it has been read.
  const viewerIsReporter = isOwnMessage(bug.reportedBy?.userId);
  const otherSideLabel = viewerIsReporter ? 'the team' : reporterName;
  const othersSeenTime = bug.othersSeenAt ? new Date(bug.othersSeenAt).getTime() : 0;
  const ownMessages = [
    ...(viewerIsReporter ? [{ key: 'report', sentAt: bug.createdAt }] : []),
    ...(bug.replies || [])
      .filter((reply) => isOwnMessage(reply.author?.userId))
      .map((reply) => ({ key: reply._id, sentAt: reply.createdAt })),
  ];
  const lastOwn = ownMessages[ownMessages.length - 1];
  const lastOwnSeen = Boolean(lastOwn && othersSeenTime >= new Date(lastOwn.sentAt).getTime());

  const renderReceipt = (messageKey) => {
    if (!lastOwn || lastOwn.key !== messageKey) return null;

    return (
      <span
        className={`bug-thread-receipt ${lastOwnSeen ? 'bug-thread-receipt--seen' : ''}`}
        title={
          lastOwnSeen
            ? `Seen by ${otherSideLabel} on ${formatDate(bug.othersSeenAt)}`
            : `Sent - ${otherSideLabel} has not opened the conversation yet`
        }
      >
        {lastOwnSeen ? <CheckCheck size={13} /> : <Check size={13} />}
        {lastOwnSeen ? `Seen ${formatSeenTime(bug.othersSeenAt)}` : 'Sent'}
      </span>
    );
  };

  return (
    <div
      className={`bug-chatbox ${closing ? 'bug-chatbox--closing' : ''}`}
      role="dialog"
      aria-label={`Bug report thread - ${reporterName}`}
    >
      <div className="bug-chatbox-header">
        <span
          className="bug-chatbox-title"
          title={isAdmin ? `${reporterName} · ${reporterEmail}` : 'Bug report thread'}
        >
          <MessageSquare size={14} />
          {isAdmin ? reporterName : 'Bug report thread'}
        </span>
        <div className="bug-chatbox-header-right">
          <span className={`bug-status-badge bug-status-select--${bug.status}`}>
            {STATUS_LABELS[bug.status] || bug.status}
          </span>
          <button
            type="button"
            className="bug-chatbox-close"
            onClick={onClose}
            aria-label="Close chat"
            title="Close chat"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="bug-chatbox-body" ref={bodyRef}>
        <div className="bug-thread-messages">
          {/* The original report opens the thread */}
          <div
            className={`bug-thread-message ${
              isOwnMessage(bug.reportedBy?.userId) ? 'bug-thread-message--mine' : ''
            }`}
          >
            <div className="bug-thread-message-head">
              <span className="bug-thread-author">
                {isOwnMessage(bug.reportedBy?.userId) ? 'You' : reporterName}
              </span>
              <span className="bug-thread-time">{formatDate(bug.createdAt)}</span>
            </div>
            <ThreadMessageBody text={bug.issue} attachments={bug.attachments} />
            {bug.newsUrl && (
              <a href={bug.newsUrl} target="_blank" rel="noreferrer" className="bug-url-link">
                {bug.newsUrl}
                <ExternalLink size={12} />
              </a>
            )}
            {/* Which build the reporter was on - first thing to check before
                chasing a bug that a refresh would have fixed */}
            {isAdmin && bug.metadata?.frontendBuild != null && (
              <span
                className="bug-thread-build"
                title={
                  bug.metadata.frontendVersion
                    ? `Reported from v${bug.metadata.frontendVersion} (build ${bug.metadata.frontendBuild})`
                    : `Reported from build ${bug.metadata.frontendBuild}`
                }
              >
                build {bug.metadata.frontendBuild}
              </span>
            )}
            {renderReceipt('report')}
          </div>

          {bug.replies?.map((reply) => (
            <div
              key={reply._id}
              className={`bug-thread-message ${
                isOwnMessage(reply.author?.userId) ? 'bug-thread-message--mine' : ''
              }`}
            >
              <div className="bug-thread-message-head">
                <span className="bug-thread-author">
                  {isOwnMessage(reply.author?.userId) ? 'You' : reply.author?.name || 'Unknown'}
                  {isAdminRole(reply.author?.role) && <span className="bug-thread-tag">Team</span>}
                </span>
                <span className="bug-thread-time">{formatDate(reply.createdAt)}</span>
              </div>
              <ThreadMessageBody text={reply.message} attachments={reply.attachments} />
              {renderReceipt(reply._id)}
            </div>
          ))}
        </div>
      </div>

      <div className="bug-chatbox-footer">
        {locked ? (
          <div className="bug-thread-locked">
            <Lock size={14} />
            <span>
              This report is {(STATUS_LABELS[bug.status] || bug.status).toLowerCase()}, so the
              thread is closed. Please raise a new report if the issue comes back.
            </span>
          </div>
        ) : (
          <form className="bug-thread-form" onSubmit={onSubmit}>
            {/* Uploaded and waiting to go out with the next message */}
            {pending.length > 0 && (
              <ul className="bug-thread-pending">
                {pending.map((attachment) => (
                  <li key={attachment.url} className="bug-thread-pending-item">
                    {isImageAttachment(attachment) ? (
                      <img src={attachment.url} alt="" className="bug-thread-pending-thumb" />
                    ) : (
                      <span className="bug-thread-pending-icon">
                        <FileText size={14} />
                      </span>
                    )}
                    <button
                      type="button"
                      className="bug-thread-pending-remove"
                      onClick={() => onRemoveAttachment(attachment.url)}
                      aria-label={`Remove ${attachment.fileName || 'attachment'}`}
                      title={`Remove ${attachment.fileName || 'attachment'}`}
                    >
                      <X size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <textarea
              className="bug-thread-input"
              placeholder="Write a reply..."
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              rows={2}
              maxLength={MAX_REPLY_LENGTH}
              disabled={sending}
            />
            <div className="bug-thread-form-actions">
              <input
                ref={fileInputRef}
                type="file"
                className="bug-thread-file-input"
                accept="image/*,application/pdf"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  // Allow re-picking the same file after a remove
                  e.target.value = '';
                  if (files.length > 0) onFilesSelected(files);
                }}
              />
              <button
                type="button"
                className="bug-thread-attach"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploading || pending.length >= MAX_CHAT_ATTACHMENTS}
                title={
                  pending.length >= MAX_CHAT_ATTACHMENTS
                    ? `You can attach up to ${MAX_CHAT_ATTACHMENTS} files per message`
                    : 'Attach an image or PDF (10MB each)'
                }
                aria-label="Attach an image or PDF"
              >
                {uploading ? <Loader2 size={15} className="spin" /> : <ImagePlus size={15} />}
              </button>

              {!isAdmin && (
                <button
                  type="button"
                  className="bug-resolve-btn"
                  onClick={onResolve}
                  disabled={sending}
                >
                  <CheckCircle2 size={14} />
                  Mark resolved
                </button>
              )}
              <button
                type="submit"
                className="bug-thread-send"
                disabled={sending || uploading || (!draft.trim() && pending.length === 0)}
              >
                {sending ? (
                  <Loader2 size={15} className="spin" />
                ) : (
                  <>
                    <Send size={14} />
                    Send
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const BugReports = () => {
  const { user } = useAuth();
  const { refreshCounts, markThreadRead } = useBugReports();
  // Admins manage every report, other users only track their own
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Own messages sit on the right of the thread, the other side on the left
  const isOwnMessage = (authorId) => Boolean(user?._id) && toId(authorId) === String(user._id);

  const [bugs, setBugs] = useState([]);
  const [counts, setCounts] = useState({ all: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Admins can keep several threads open at once, other users only one
  const [threadBugs, setThreadBugs] = useState([]);
  const [closingIds, setClosingIds] = useState([]);
  const [replyDrafts, setReplyDrafts] = useState({});
  // Attachments already uploaded to GCS, waiting to go out with the next reply
  const [replyAttachments, setReplyAttachments] = useState({});
  const [uploadingIds, setUploadingIds] = useState([]);
  const [sendingIds, setSendingIds] = useState([]);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [toast, setToast] = useState(null);
  const threadBodyRefs = useRef({});
  const closeTimersRef = useRef({});
  // Boxes whose scroll has been pinned to the bottom at least once
  const pinnedThreadsRef = useRef({});
  // Open threads as the poll sees them - kept in a ref so the poll callback
  // stays stable and its interval is not torn down on every update
  const openThreadsRef = useRef([]);
  const hasOpenThreads = threadBugs.length > 0;

  openThreadsRef.current = threadBugs;

  // Debounce the search box so we do not fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchBugs = useCallback(
    async (pageToFetch, append) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await bugService.getBugReports({
          page: pageToFetch,
          limit: LIMIT,
          status: statusFilter,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });

        if (response.success) {
          const fetched = response.data?.bugs || [];
          setBugs((prev) => (append ? [...prev, ...fetched] : fetched));
          setCounts(response.data?.counts || { all: 0 });
          setPagination(response.pagination || null);
          setError(null);
        } else {
          setError(response.message || 'Failed to fetch bug reports');
        }
      } catch (err) {
        console.error('Error fetching bug reports:', err);
        setError(err.response?.data?.message || 'Failed to fetch bug reports');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [statusFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchBugs(1, false);
  }, [fetchBugs]);

  const handleLoadMore = () => {
    if (!pagination?.hasNextPage || loadingMore) return;
    fetchBugs(pagination.page + 1, true);
  };

  // Keep the list row and any open chat box in sync after a reply / status change
  const applyBugUpdate = (updated) => {
    if (!updated) return;
    setBugs((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
    setThreadBugs((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
  };

  const handleStatusChange = async (bug, status) => {
    if (status === bug.status) return;

    setUpdatingId(bug._id);
    try {
      const response = await bugService.updateBugStatus(bug._id, status);

      if (response.success) {
        applyBugUpdate(response.data.bugReport);
        setToast({ message: 'Status updated', type: 'success' });
        // Counts and the active filter list can change with the new status
        fetchBugs(1, false);
        refreshCounts();
      } else {
        setToast({ message: response.message || 'Failed to update status', type: 'error' });
      }
    } catch (err) {
      console.error('Error updating bug status:', err);
      setToast({
        message: err.response?.data?.message || 'Failed to update status',
        type: 'error',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const openThread = (bug) => {
    clearTimeout(closeTimersRef.current[bug._id]);
    delete closeTimersRef.current[bug._id];
    setClosingIds((prev) => prev.filter((id) => id !== bug._id));

    // Opening the conversation is what marks its messages read - stamped even
    // with nothing unread, since that stamp is what shows the other side a
    // "Seen" receipt on messages this user had already read elsewhere
    if (bug.unreadCount > 0) {
      setBugs((prev) =>
        prev.map((item) => (item._id === bug._id ? { ...item, unreadCount: 0 } : item))
      );
    }
    markThreadRead(bug._id);

    setThreadBugs((prev) => {
      // Already on screen - nothing to open
      if (prev.some((item) => item._id === bug._id)) return prev;
      // Non-admin users only ever have one thread open
      if (!isAdmin) return [bug];
      // Admins stack boxes, dropping the oldest once the dock is full
      return [...prev, bug].slice(-MAX_OPEN_CHATBOXES);
    });
  };

  // Let a box slide back down before it leaves the DOM
  const closeThread = useCallback(
    (bugId) => {
      if (sendingIds.includes(bugId) || uploadingIds.includes(bugId) || closingIds.includes(bugId))
        return;

      setClosingIds((prev) => [...prev, bugId]);
      closeTimersRef.current[bugId] = setTimeout(() => {
        setThreadBugs((prev) => prev.filter((item) => item._id !== bugId));
        setClosingIds((prev) => prev.filter((id) => id !== bugId));
        setReplyDrafts((prev) => {
          const next = { ...prev };
          delete next[bugId];
          return next;
        });
        setReplyAttachments((prev) => {
          const next = { ...prev };
          delete next[bugId];
          return next;
        });
        delete threadBodyRefs.current[bugId];
        delete pinnedThreadsRef.current[bugId];
        delete closeTimersRef.current[bugId];
      }, CHATBOX_CLOSE_MS);
    },
    [sendingIds, uploadingIds, closingIds]
  );

  useEffect(
    () => () => Object.values(closeTimersRef.current).forEach((timer) => clearTimeout(timer)),
    []
  );

  // Escape closes the box that was opened last
  useEffect(() => {
    if (threadBugs.length === 0) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeThread(threadBugs[threadBugs.length - 1]._id);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [threadBugs, closeThread]);

  // Stick to the newest message as threads open and grow, but leave the scroll
  // alone when a poll arrives while someone is reading further up
  useEffect(() => {
    threadBugs.forEach((bug) => {
      const body = threadBodyRefs.current[bug._id];
      if (!body) return;

      const nearBottom =
        body.scrollHeight - body.scrollTop - body.clientHeight < SCROLL_PIN_SLACK_PX;

      if (!pinnedThreadsRef.current[bug._id] || nearBottom) {
        body.scrollTop = body.scrollHeight;
        pinnedThreadsRef.current[bug._id] = true;
      }
    });
  }, [threadBugs]);

  // Pull open threads so replies and read receipts land without a page reload
  const refreshOpenThreads = useCallback(async () => {
    const open = openThreadsRef.current;
    if (open.length === 0) return;

    const responses = await Promise.all(
      open.map((bug) => bugService.getBugReport(bug._id).catch(() => null))
    );

    responses.forEach((response, index) => {
      const fetched = response?.success ? response.data?.bugReport : null;
      if (!fetched) return;

      // The box is on screen, so anything that arrived is already being read
      if (fetched.unreadCount > 0) {
        markThreadRead(fetched._id);
      }

      const current = open[index];
      if (threadSignature(current) === threadSignature(fetched)) return;

      applyBugUpdate({ ...fetched, unreadCount: 0 });
    });
  }, [markThreadRead]);

  // Poll only while a box is open, and only while the tab is in front
  useEffect(() => {
    if (!hasOpenThreads) return;

    let timer = setInterval(refreshOpenThreads, THREAD_POLL_MS);

    const handleVisibility = () => {
      clearInterval(timer);
      if (!document.hidden) {
        refreshOpenThreads();
        timer = setInterval(refreshOpenThreads, THREAD_POLL_MS);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hasOpenThreads, refreshOpenThreads]);

  // Upload one chat attachment to GCS through a signed URL, same as the report form
  const uploadThreadFile = async (file) => {
    const response = await bugService.getUploadSignedUrl(file.name, file.type, file.size);

    if (!response.success) {
      throw new Error(response.message || 'Failed to get upload URL');
    }

    const { signedUrl, publicUrl } = response.data;

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.onload = () => (xhr.status === 200 ? resolve() : reject(new Error('Upload failed')));
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });

    return { url: publicUrl, fileName: file.name, contentType: file.type };
  };

  // Files picked in a chat composer - uploaded right away, sent with the reply
  const handleThreadFiles = async (bug, files) => {
    const pending = replyAttachments[bug._id] || [];

    if (pending.length + files.length > MAX_CHAT_ATTACHMENTS) {
      setToast({
        message: `You can attach up to ${MAX_CHAT_ATTACHMENTS} files per message`,
        type: 'error',
      });
      return;
    }

    if (files.some((file) => !VALID_CHAT_TYPES.includes(file.type))) {
      setToast({ message: 'Only images and PDF files are allowed', type: 'error' });
      return;
    }

    if (files.some((file) => file.size > MAX_CHAT_FILE_SIZE)) {
      setToast({ message: 'Each file must be 10MB or smaller', type: 'error' });
      return;
    }

    setUploadingIds((prev) => [...prev, bug._id]);
    try {
      const uploaded = await Promise.all(files.map(uploadThreadFile));
      setReplyAttachments((prev) => ({ ...prev, [bug._id]: [...(prev[bug._id] || []), ...uploaded] }));
    } catch (err) {
      console.error('Error uploading chat attachment:', err);
      setToast({ message: 'Failed to upload attachment', type: 'error' });
    } finally {
      setUploadingIds((prev) => prev.filter((id) => id !== bug._id));
    }
  };

  const handleRemoveThreadFile = (bugId, url) => {
    setReplyAttachments((prev) => ({
      ...prev,
      [bugId]: (prev[bugId] || []).filter((attachment) => attachment.url !== url),
    }));
  };

  const handleAddReply = async (e, bug) => {
    e.preventDefault();

    if (sendingIds.includes(bug._id)) return;

    if (uploadingIds.includes(bug._id)) {
      setToast({ message: 'Please wait for the attachment to finish uploading', type: 'info' });
      return;
    }

    const message = (replyDrafts[bug._id] || '').trim();
    const attachments = replyAttachments[bug._id] || [];

    if (!message && attachments.length === 0) {
      setToast({ message: 'Please write a message or attach a file', type: 'error' });
      return;
    }

    setSendingIds((prev) => [...prev, bug._id]);
    try {
      const response = await bugService.addBugReply(bug._id, message, attachments);

      if (response.success) {
        applyBugUpdate(response.data.bugReport);
        setReplyDrafts((prev) => ({ ...prev, [bug._id]: '' }));
        setReplyAttachments((prev) => ({ ...prev, [bug._id]: [] }));
        // Your own message always jumps into view, even if you had scrolled up
        delete pinnedThreadsRef.current[bug._id];
      } else {
        setToast({ message: response.message || 'Failed to send reply', type: 'error' });
      }
    } catch (err) {
      console.error('Error adding bug reply:', err);
      setToast({
        message: err.response?.data?.message || 'Failed to send reply',
        type: 'error',
      });
    } finally {
      setSendingIds((prev) => prev.filter((id) => id !== bug._id));
    }
  };

  // Reporters can close their own report out, but they can never reopen it
  const handleResolve = async () => {
    if (!resolveTarget) return;

    setResolving(true);
    try {
      const response = await bugService.updateBugStatus(resolveTarget._id, 'resolved');

      if (response.success) {
        applyBugUpdate(response.data.bugReport);
        setResolveTarget(null);
        setToast({ message: 'Report marked as resolved', type: 'success' });
        fetchBugs(1, false);
        refreshCounts();
      } else {
        setToast({ message: response.message || 'Failed to mark as resolved', type: 'error' });
      }
    } catch (err) {
      console.error('Error resolving bug report:', err);
      setToast({
        message: err.response?.data?.message || 'Failed to mark as resolved',
        type: 'error',
      });
    } finally {
      setResolving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const response = await bugService.deleteBug(deleteTarget._id);

      if (response.success) {
        setBugs((prev) => prev.filter((item) => item._id !== deleteTarget._id));
        setDeleteTarget(null);
        setToast({ message: 'Bug report deleted', type: 'success' });
        fetchBugs(1, false);
        refreshCounts();
      } else {
        setToast({ message: response.message || 'Failed to delete bug report', type: 'error' });
      }
    } catch (err) {
      console.error('Error deleting bug report:', err);
      setToast({
        message: err.response?.data?.message || 'Failed to delete bug report',
        type: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading && bugs.length === 0) {
    return (
      <div className="bug-reports-page">
        <div className="bug-reports-loading">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bug-reports-page">
        <div className="bug-reports-toolbar">
          <div className="bug-reports-filters">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`bug-filter-chip ${statusFilter === filter.key ? 'bug-filter-chip--active' : ''}`}
                onClick={() => setStatusFilter(filter.key)}
              >
                {filter.label}
                <span className="bug-filter-count">
                  {filter.key === 'all' ? counts.all || 0 : counts[filter.key] || 0}
                </span>
              </button>
            ))}
          </div>

          <div className="bug-search-wrapper">
            <Search size={16} className="bug-search-icon" />
            <input
              type="text"
              className="bug-search-input"
              placeholder={isAdmin ? 'Search issue, news URL, user...' : 'Search issue or news URL...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && <div className="bug-reports-error">{error}</div>}

        {!error && bugs.length === 0 ? (
          <div className="bug-reports-empty">
            {isAdmin
              ? 'No bug reports found'
              : 'You have not reported any bugs yet. Use the Report Bug button to raise one.'}
          </div>
        ) : (
          <div className="bug-reports-table-container">
            <table className="bug-reports-table">
              <thead>
                <tr>
                  {isAdmin && <th>Reported By</th>}
                  <th>Issue</th>
                  <th>News URL</th>
                  <th>Attachments</th>
                  <th>Reported At</th>
                  <th>Status</th>
                  <th>Thread</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bugs.map((bug) => (
                  <tr key={bug._id}>
                    {isAdmin && (
                      <td className="bug-user-cell">
                        <div className="bug-user-name">{bug.reportedBy?.name || 'Unknown'}</div>
                        <div className="bug-user-email">{bug.reportedBy?.email || '-'}</div>
                      </td>
                    )}
                    <td className="bug-issue-cell">{bug.issue}</td>
                    <td className="bug-url-cell">
                      {bug.newsUrl ? (
                        <a
                          href={bug.newsUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="bug-url-link"
                          title={bug.newsUrl}
                        >
                          {bug.newsUrl}
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span className="bug-empty-value">-</span>
                      )}
                    </td>
                    <td className="bug-attachments-cell">
                      {bug.attachments?.length > 0 ? (
                        <div className="bug-attachment-list">
                          {bug.attachments.map((attachment, index) => (
                            <a
                              key={attachment.url}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              className="bug-attachment-link"
                              title={attachment.fileName || `Attachment ${index + 1}`}
                            >
                              {attachment.contentType?.startsWith('image/') ? (
                                <img src={attachment.url} alt="" className="bug-attachment-thumb" />
                              ) : (
                                <span className="bug-attachment-file">
                                  <FileText size={14} />
                                </span>
                              )}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="bug-empty-value">
                          <Paperclip size={14} /> None
                        </span>
                      )}
                    </td>
                    <td className="bug-date-cell">{formatDate(bug.createdAt)}</td>
                    <td className="bug-status-cell">
                      {isAdmin ? (
                        <>
                          <select
                            className={`bug-status-select bug-status-select--${bug.status}`}
                            value={bug.status}
                            onChange={(e) => handleStatusChange(bug, e.target.value)}
                            disabled={updatingId === bug._id}
                          >
                            {STATUS_OPTIONS.map((option) => (
                              <option key={option.key} value={option.key}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {updatingId === bug._id && <Loader2 size={14} className="spin" />}
                        </>
                      ) : (
                        <span className={`bug-status-badge bug-status-select--${bug.status}`}>
                          {STATUS_LABELS[bug.status] || bug.status}
                        </span>
                      )}
                    </td>
                    <td className="bug-thread-cell">
                      <button
                        type="button"
                        className={`bug-thread-btn ${
                          bug.unreadCount > 0
                            ? 'bug-thread-btn--unread'
                            : bug.replies?.length
                              ? 'bug-thread-btn--active'
                              : ''
                        }`}
                        onClick={() => openThread(bug)}
                        title={
                          bug.unreadCount > 0
                            ? `${bug.unreadCount} unread ${bug.unreadCount > 1 ? 'messages' : 'message'}`
                            : isThreadLocked(bug)
                              ? 'View conversation'
                              : 'Open conversation'
                        }
                      >
                        <MessageSquare size={14} />
                        {bug.replies?.length || 0}
                        {bug.unreadCount > 0 && (
                          <span className="bug-thread-unread">+{bug.unreadCount}</span>
                        )}
                      </button>
                    </td>
                    <td className="bug-actions-cell">
                      {isAdmin ? (
                        <button
                          type="button"
                          className="bug-delete-btn"
                          onClick={() => setDeleteTarget(bug)}
                          aria-label="Delete bug report"
                        >
                          <Trash2 size={15} />
                        </button>
                      ) : !isThreadLocked(bug) ? (
                        <button
                          type="button"
                          className="bug-resolve-btn"
                          onClick={() => setResolveTarget(bug)}
                        >
                          <CheckCircle2 size={14} />
                          Mark resolved
                        </button>
                      ) : (
                        <span className="bug-empty-value">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination?.hasNextPage && (
          <div className="bug-load-more-wrapper">
            <button
              type="button"
              className="bug-load-more"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? <Loader2 size={15} className="spin" /> : 'Load more'}
            </button>
          </div>
        )}
      </div>

      {threadBugs.length > 0 && (
        <div className="bug-chatbox-dock">
          {threadBugs.map((bug) => (
            <BugThreadChatbox
              key={bug._id}
              bug={bug}
              isAdmin={isAdmin}
              isOwnMessage={isOwnMessage}
              draft={replyDrafts[bug._id] || ''}
              onDraftChange={(value) =>
                setReplyDrafts((prev) => ({ ...prev, [bug._id]: value }))
              }
              attachments={replyAttachments[bug._id] || []}
              uploading={uploadingIds.includes(bug._id)}
              onFilesSelected={(files) => handleThreadFiles(bug, files)}
              onRemoveAttachment={(url) => handleRemoveThreadFile(bug._id, url)}
              sending={sendingIds.includes(bug._id)}
              closing={closingIds.includes(bug._id)}
              bodyRef={(el) => {
                threadBodyRefs.current[bug._id] = el;
              }}
              onClose={() => closeThread(bug._id)}
              onSubmit={(e) => handleAddReply(e, bug)}
              onResolve={() => setResolveTarget(bug)}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={Boolean(resolveTarget)}
        onClose={() => !resolving && setResolveTarget(null)}
        title="Mark as resolved"
        size="small"
      >
        <p className="bug-delete-text">
          Mark this report as resolved? You will not be able to reopen it or reply on the thread
          afterwards - you would have to raise a new report.
        </p>
        <div className="bug-delete-actions">
          <button
            type="button"
            className="bug-delete-cancel"
            onClick={() => setResolveTarget(null)}
            disabled={resolving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bug-resolve-confirm"
            onClick={handleResolve}
            disabled={resolving}
          >
            {resolving ? <Loader2 size={15} className="spin" /> : 'Mark resolved'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete bug report"
        size="small"
      >
        <p className="bug-delete-text">
          Are you sure you want to delete this bug report from{' '}
          <strong>{deleteTarget?.reportedBy?.email || 'this user'}</strong>? This cannot be undone.
        </p>
        <div className="bug-delete-actions">
          <button
            type="button"
            className="bug-delete-cancel"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bug-delete-confirm"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <Loader2 size={15} className="spin" /> : 'Delete'}
          </button>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};

export default BugReports;
